<script lang="ts">
  /**
   * The card stack (brief §9a, §14).
   *
   * Four directions, and **every one of them also has a button and a key**. An interface that
   * only listens for swipes is unusable on a desktop and unusable with a screen reader, and
   * this is a stack of yes/no questions — the least defensible place to make the gesture the
   * only way in.
   */
  import type { Attribute } from '$domain/attributes.ts';
  import { describeCrowd, SWIPES, type Swipe, verdictOf } from '$domain/passby.ts';
  import { deck } from './deck.svelte.ts';
  import { gdiColour, gdiLabel, vibeSymbol } from './gdi-scale.ts';

  type Props = {
    attributes: Attribute[];
    onclose: () => void;
    /** The up-swipe: open the report sheet with this cell pre-filled. */
    onrate: (cell: string) => void;
  };

  const { attributes, onclose, onrate }: Props = $props();

  /** Pixels of travel before a drag counts as a gesture rather than a wobble. */
  const THRESHOLD = 80;

  let drag = $state<{ x: number; y: number } | undefined>();
  let start: { x: number; y: number } | undefined;

  const card = $derived(deck.top);
  const byKey = $derived(new Map(attributes.map((entry) => [entry.key, entry])));

  const hint = $derived.by(() => {
    if (drag === undefined) return undefined;
    const { x, y } = drag;
    if (Math.abs(x) < THRESHOLD && Math.abs(y) < THRESHOLD) return undefined;
    if (Math.abs(x) > Math.abs(y)) return x > 0 ? SWIPES.good : SWIPES.lame;
    return y < 0 ? SWIPES.rate : SWIPES.skip;
  });

  const LABELS: Record<Swipe, string> = {
    good: 'War gut',
    lame: 'Voll lame',
    rate: 'Selbst bewerten',
    skip: 'Später',
  };

  function answer(swipe: Swipe) {
    const target = card;
    if (target === undefined) return;
    drag = undefined;
    start = undefined;
    if (swipe === SWIPES.rate) {
      onrate(target.cell);
      return;
    }
    void deck.swipe(target.cell, swipe);
  }

  function onPointerDown(event: PointerEvent) {
    start = { x: event.clientX, y: event.clientY };
    drag = { x: 0, y: 0 };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent) {
    if (start === undefined) return;
    drag = { x: event.clientX - start.x, y: event.clientY - start.y };
  }

  function onPointerUp() {
    const gesture = hint;
    start = undefined;
    drag = undefined;
    if (gesture !== undefined) answer(gesture);
  }

  function onKey(event: KeyboardEvent) {
    const map: Record<string, Swipe> = {
      ArrowRight: SWIPES.good,
      ArrowLeft: SWIPES.lame,
      ArrowUp: SWIPES.rate,
      ArrowDown: SWIPES.skip,
    };
    const swipe = map[event.key];
    if (swipe === undefined) {
      if (event.key === 'Escape') onclose();
      return;
    }
    event.preventDefault();
    answer(swipe);
  }

  const age = (at: number | null) => {
    if (at === null) return 'keine frische Meldung';
    const hours = Math.round((Date.now() - at) / 3_600_000);
    if (hours < 1) return 'gerade eben';
    if (hours < 24) return `vor ${hours} h`;
    return `vor ${Math.round(hours / 24)} Tagen`;
  };
</script>

<svelte:window onkeydown={onKey} />

<div class="backdrop" role="presentation" onclick={onclose}></div>

<section class="stack" aria-label="Orte bewerten">
  <header>
    <h2>Warst du hier?</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  {#if card === undefined}
    <p class="empty">
      Gerade nichts zu bewerten. Karten tauchen auf, wenn du eine Minute an einem Ort stehst,
      an dem schon etwas gemeldet ist.
    </p>
  {:else}
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <article
      class="card"
      class:dragging={drag !== undefined}
      style={`--dx: ${drag?.x ?? 0}px; --dy: ${drag?.y ?? 0}px; --rot: ${(drag?.x ?? 0) / 25}deg; --gdi-colour: ${gdiColour(card.gdi)}`}
      onpointerdown={onPointerDown}
      onpointermove={onPointerMove}
      onpointerup={onPointerUp}
      onpointercancel={onPointerUp}
    >
      <div class="gdi">
        <strong>{card.gdi}</strong>
        <span>{gdiLabel(card.gdi)}</span>
      </div>
      <p class="meta">
        {card.sightingCount} Meldungen · zuletzt {age(card.lastSightingAt)}
        {#if card.vibe !== null}· {vibeSymbol(card.vibe)}{/if}
      </p>
      {#if card.topAttribute}
        <p class="meta">
          Am stärksten: {byKey.get(card.topAttribute)?.emoji ?? ''}
          {byKey.get(card.topAttribute)?.label ?? card.topAttribute}
        </p>
      {/if}

      {#if hint !== undefined}
        <span class="hint hint-{hint}">{LABELS[hint]}</span>
      {/if}
    </article>

    <div class="actions">
      <button onclick={() => answer(SWIPES.lame)} aria-keyshortcuts="ArrowLeft">
        👎 {LABELS.lame}
      </button>
      <button onclick={() => answer(SWIPES.skip)} aria-keyshortcuts="ArrowDown">
        ⏭ {LABELS.skip}
      </button>
      <button onclick={() => answer(SWIPES.rate)} aria-keyshortcuts="ArrowUp">
        ✍ {LABELS.rate}
      </button>
      <button onclick={() => answer(SWIPES.good)} aria-keyshortcuts="ArrowRight">
        👍 {LABELS.good}
      </button>
    </div>

    <p class="keys">Pfeiltasten gehen auch: ← lame, → gut, ↑ selbst bewerten, ↓ später.</p>
  {/if}

  {#if deck.error}<p class="error" role="alert">{deck.error}</p>{/if}

  {#if deck.undoable !== undefined}
    <p class="undo" role="status">
      {LABELS[deck.undoable.swipe]} gespeichert.
      <button onclick={() => deck.undo()} disabled={deck.busy}>Rückgängig</button>
    </p>
  {/if}

  <!--
    Schwarmgefühl (amendment 12). Shown only after the undo window, never during it: a verdict
    you could still take back is a verdict you could shop around for. The leading emoji says
    which swipe it answers, because by now the next card may already be on top.
  -->
  {#if deck.revealed !== undefined}
    <p class="reveal reveal-{verdictOf(deck.revealed)}" role="status">
      <span aria-hidden="true">{deck.revealed.vote === 1 ? '👍' : '👎'}</span>
      {describeCrowd(deck.revealed.vote, deck.revealed)}
    </p>
  {/if}
</section>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 55%);
    z-index: 7;
  }

  .stack {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 8;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: min(24rem, calc(100% - 2rem));
    padding: 1rem;
    border: 1px solid var(--border);
    border-radius: 1.25rem;
    background: var(--card);
    box-shadow: 0 12px 48px rgb(0 0 0 / 40%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .card {
    position: relative;
    display: grid;
    gap: 0.4rem;
    padding: 1.25rem 1rem;
    border: 1px solid var(--gdi-colour);
    border-radius: 1rem;
    background: color-mix(in srgb, var(--gdi-colour) 12%, var(--bg));
    touch-action: none;
    cursor: grab;
    transform: translate(var(--dx), var(--dy)) rotate(var(--rot));
  }

  .card:not(.dragging) {
    transition: transform 0.2s ease-out;
  }

  .gdi {
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
  }

  .gdi strong {
    font-size: 2.5rem;
    line-height: 1;
    color: var(--gdi-colour);
  }

  .gdi span {
    font-size: 0.9375rem;
  }

  .meta {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .hint {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    background: var(--accent);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .hint-lame {
    right: auto;
    left: 0.75rem;
    background: #4a4f57;
  }

  .hint-rate {
    background: #d8a018;
  }

  .hint-skip {
    top: auto;
    bottom: 0.75rem;
    background: #4a4f57;
  }

  .actions {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.4rem;
  }

  .actions button {
    padding: 0.6rem 0.4rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .keys,
  .empty,
  .undo,
  .error {
    margin: 0;
    font-size: 0.75rem;
    color: var(--muted);
    text-align: center;
  }

  .empty {
    font-size: 0.875rem;
    padding: 1.5rem 0.5rem;
  }

  .error {
    color: var(--accent);
  }

  .reveal {
    margin: 0;
    padding: 0.55rem 0.75rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    background: var(--bg);
    font-size: 0.8125rem;
    text-align: center;
    animation: arrive 0.25s ease-out;
  }

  .reveal-agreed {
    border-color: #2e9d5b;
    background: color-mix(in srgb, #2e9d5b 14%, var(--bg));
  }

  .reveal-disagreed {
    border-color: #d8a018;
    background: color-mix(in srgb, #d8a018 12%, var(--bg));
  }

  @keyframes arrive {
    from {
      opacity: 0;
      transform: translateY(0.4rem);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .reveal {
      animation: none;
    }
  }

  .undo button {
    margin-left: 0.4rem;
    border: 0;
    background: none;
    color: var(--accent);
    font-size: 0.75rem;
    text-decoration: underline;
    cursor: pointer;
  }
</style>
